import React, { useEffect } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AssignmentIndOutlinedIcon from '@mui/icons-material/AssignmentIndOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ShareOutlinedIcon from '@mui/icons-material/ShareOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import AddIcon from '@mui/icons-material/Add';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useDispatch, useSelector } from 'react-redux';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ROUTES } from '../../../types/global/routes.types';
import { fetchBrandOpsHomeStats } from '../../../redux/brandOps/actions';
import {
    brandOpsIsBrandAdminSelector,
    brandOpsHomeStatsSelector,
    brandOpsFetchingHomeStatsSelector,
} from '../../../redux/brandOps/selectors';
import { BRAND_LUXE_MUTED, BRAND_LUXE_PRIMARY, BRAND_SURFACE_CONTAINER_HIGH } from '../brandDashboardTokens';
import { BrandManagePromoBanner } from './BrandManagePromoBanner';
import { BrandManageGate, MANAGE_PAGE_SX, useBrandManageGate } from './manageShared';

// ── tokens ────────────────────────────────────────────────────────────────────
const C_SURFACE = '#1a1919';
const C_SURFACE_HIGH = '#201f1f';
const C_BORDER = 'rgba(255,255,255,0.08)';
const C_AMBER = BRAND_LUXE_PRIMARY;
const C_MUTED = BRAND_LUXE_MUTED;

// ── helpers ───────────────────────────────────────────────────────────────────
function formatCurrency(cents: number, currency: string): string {
    const sym = currency === 'NGN' ? '₦' : '$';
    const val = cents / 100;
    if (val >= 1_000_000) return `${sym}${(val / 1_000_000).toFixed(1)}M`;
    if (val >= 1000) return `${sym}${(val / 1000).toFixed(1)}k`;
    return `${sym}${val.toLocaleString()}`;
}

function formatTime(iso: string | null): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (isNaN(d.getTime())) return null;
    const now = new Date();
    const isToday =
        d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return isToday ? `Today ${time}` : d.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + time;
}

// ── Stat tile ─────────────────────────────────────────────────────────────────
function StatTile({
    icon,
    label,
    value,
    sub,
    loading,
}: {
    icon: React.ReactNode;
    label: string;
    value: string;
    sub?: string;
    loading?: boolean;
}) {
    return (
        <Box
            sx={{
                flex: '1 1 180px',
                p: '20px 24px',
                borderRadius: 2.5,
                bgcolor: C_SURFACE,
                border: `1px solid ${C_BORDER}`,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
            }}>
            {/* label + icon row */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography
                    sx={{
                        color: C_MUTED,
                        fontSize: 11,
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        textTransform: 'uppercase',
                        letterSpacing: '0.09em',
                    }}>
                    {label}
                </Typography>
                <Box
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1.5,
                        bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: C_AMBER,
                    }}>
                    {icon}
                </Box>
            </Box>

            {/* big number */}
            {loading ? (
                <Skeleton variant="text" width={80} height={44} sx={{ bgcolor: 'rgba(255,255,255,0.06)' }} />
            ) : (
                <Typography
                    sx={{
                        color: '#fff',
                        fontSize: 36,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        lineHeight: 1,
                        letterSpacing: '-0.5px',
                    }}>
                    {value}
                </Typography>
            )}

            {/* sub line */}
            {sub && !loading && (
                <Typography
                    sx={{ color: C_AMBER, fontSize: 11, fontFamily: FONT_ENUM.MAIN_MEDIUM, letterSpacing: '0.02em' }}>
                    {sub}
                </Typography>
            )}
        </Box>
    );
}

// ── Module card ───────────────────────────────────────────────────────────────
function ModuleCard({
    to,
    Icon,
    title,
    blurb,
    badge,
    badgeColor,
    meta,
    cta,
}: {
    to: string;
    Icon: typeof GroupsOutlinedIcon;
    title: string;
    blurb: string;
    badge?: string;
    badgeColor?: string;
    meta?: string;
    cta?: string;
}) {
    const bc = badgeColor || '#34d399';
    return (
        <Box
            sx={{
                borderRadius: 2.5,
                bgcolor: C_SURFACE,
                border: `1px solid ${C_BORDER}`,
                overflow: 'hidden',
                transition: 'border-color 0.2s, background 0.2s',
                '&:hover': { borderColor: 'rgba(255,201,101,0.3)', bgcolor: '#1e1d1d' },
                display: 'flex',
                flexDirection: 'column',
            }}>
            <Box
                component={RouterLink}
                to={to}
                sx={{ display: 'flex', flexDirection: 'column', flex: 1, p: '24px 24px 16px', textDecoration: 'none' }}>
                {/* Header row */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            bgcolor: BRAND_SURFACE_CONTAINER_HIGH,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: C_AMBER,
                        }}>
                        <Icon sx={{ fontSize: 22 }} />
                    </Box>
                    {badge && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 1.25,
                                py: 0.4,
                                borderRadius: 5,
                                bgcolor: `${bc}12`,
                                border: `1px solid ${bc}30`,
                            }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: bc, flexShrink: 0 }} />
                            <Typography
                                sx={{
                                    color: bc,
                                    fontSize: 11.5,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    letterSpacing: '0.02em',
                                }}>
                                {badge}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Title */}
                <Typography
                    sx={{
                        color: '#fff',
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: 18,
                        letterSpacing: '-0.2px',
                        mb: 0.75,
                        lineHeight: 1.2,
                    }}>
                    {title}
                </Typography>

                {/* Blurb */}
                <Typography
                    sx={{
                        color: 'rgba(255,255,255,0.45)',
                        fontSize: 13,
                        lineHeight: 1.6,
                        flex: 1,
                    }}>
                    {blurb}
                </Typography>
            </Box>

            {/* Footer */}
            {(meta || cta) && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 3,
                        pb: 2.5,
                        pt: 0,
                    }}>
                    {meta ? (
                        <Typography
                            sx={{
                                color: 'rgba(255,255,255,0.28)',
                                fontSize: 12,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            }}>
                            {meta}
                        </Typography>
                    ) : (
                        <Box />
                    )}
                    {cta && (
                        <Box
                            component={RouterLink}
                            to={to}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                color: C_AMBER,
                                fontSize: 13,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                textDecoration: 'none',
                                letterSpacing: '0.01em',
                                '&:hover': { opacity: 0.8 },
                            }}>
                            {cta} <ArrowForwardIcon sx={{ fontSize: 14 }} />
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
}

// ── Quick action button ───────────────────────────────────────────────────────
function QuickBtn({
    to,
    icon,
    label,
    primary,
}: {
    to: string;
    icon: React.ReactNode;
    label: string;
    primary?: boolean;
}) {
    return (
        <Box
            component={RouterLink}
            to={to}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 2.25,
                py: 1.1,
                borderRadius: 2,
                textDecoration: 'none',
                fontFamily: FONT_ENUM.MAIN_BOLD,
                fontSize: 13.5,
                letterSpacing: '0.01em',
                transition: 'all 0.15s',
                ...(primary
                    ? {
                          background: `linear-gradient(135deg, ${C_AMBER} 0%, #ffb830 100%)`,
                          color: '#1a1100',
                          '&:hover': { opacity: 0.88 },
                      }
                    : {
                          bgcolor: C_SURFACE_HIGH,
                          border: `1px solid ${C_BORDER}`,
                          color: 'rgba(255,255,255,0.85)',
                          '&:hover': { borderColor: 'rgba(255,255,255,0.22)', color: '#fff' },
                      }),
            }}>
            {icon}
            {label}
        </Box>
    );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function BrandManageHome() {
    const dispatch = useDispatch();
    const { brandId, brand } = useBrandManageGate();
    const isAdmin = useSelector(brandOpsIsBrandAdminSelector);
    const stats = useSelector(brandOpsHomeStatsSelector);
    const loadingStats = useSelector(brandOpsFetchingHomeStatsSelector);

    useEffect(() => {
        if (brandId) dispatch(fetchBrandOpsHomeStats({ brandId }));
    }, [brandId, dispatch]);

    const escrowDisplay = stats ? formatCurrency(stats.total_escrow_cents, stats.currency) : '—';
    const nextPost = stats?.next_scheduled_at ? formatTime(stats.next_scheduled_at) : null;

    const adminModules = [
        {
            to: ROUTES.BRAND_MANAGE_TEAM,
            Icon: GroupsOutlinedIcon,
            title: 'Team & Access',
            blurb: 'Invite managers, configure role tiers (Ops Manager, Strategist), and partition OAuth token delegations.',
            badge: stats ? `${stats.active_manager_count} Active` : undefined,
            badgeColor: '#34d399',
            cta: 'Configure Roles',
        },
        {
            to: ROUTES.BRAND_MANAGE_CONTRACTS,
            Icon: DescriptionOutlinedIcon,
            title: 'Contracts & Escrow',
            blurb: 'Create, dispatch, and track MSA signatures with automated escrow locks and milestone verification pipelines.',
            badge: stats ? `${stats.live_contract_count} Live` : undefined,
            badgeColor: C_AMBER,
            meta: stats && stats.live_contract_count > 0 ? `${escrowDisplay} locked` : undefined,
            cta: 'Manage Vault',
        },
        {
            to: ROUTES.BRAND_MANAGE_CALENDAR,
            Icon: CalendarMonthOutlinedIcon,
            title: 'Content Calendar',
            blurb: 'Plan omnichannel drops, visual approval pipeline, and instant scheduling across global timezones.',
            badge: stats ? `${stats.scheduled_post_count} Queued` : undefined,
            badgeColor: '#818cf8',
            meta: nextPost ? `Next: ${nextPost}` : undefined,
            cta: 'Open Timeline',
        },
        {
            to: ROUTES.BRAND_MANAGE_SOCIAL,
            Icon: ShareOutlinedIcon,
            title: 'Social Accounts',
            blurb: 'Connect Instagram, TikTok, and YouTube with continuous token heartbeat verification and analytics ingestion.',
            cta: 'Token Health',
        },
        {
            to: ROUTES.BRAND_MANAGE_AI,
            Icon: AutoAwesomeOutlinedIcon,
            title: 'AI DM Engine',
            blurb: 'Knowledge vault & automated Instagram DM response playground. Real-time creator rate card parsing and intent triage.',
            badge: stats && stats.auto_reply_count > 0 ? `Active SLA` : undefined,
            badgeColor: '#34d399',
            meta: stats && stats.auto_reply_count > 0 ? `${stats.auto_reply_count} replied (30d)` : undefined,
            cta: 'Test Prompt',
        },
        {
            to: ROUTES.BRAND_MANAGE_UGC,
            Icon: PhotoLibraryOutlinedIcon,
            title: 'Content Library',
            blurb: 'Browse approved UGC from live campaigns, rights-cleared master assets, b-roll footage, and high-res raw cuts.',
            cta: 'Browse Vault',
        },
        {
            to: ROUTES.BRAND_MANAGE_INBOX,
            Icon: InboxOutlinedIcon,
            title: 'Inbox',
            blurb: 'Instagram DMs and comment-to-DM automation activity.',
            cta: 'Open Inbox',
        },
        {
            to: ROUTES.BRAND_MANAGE_SETTINGS,
            Icon: SettingsOutlinedIcon,
            title: 'Settings',
            blurb: 'Approval rules, notifications, currency defaults, and role permissions.',
            cta: 'Configure',
        },
    ];

    const managerModules = [
        {
            to: ROUTES.BRAND_MANAGE_MY_CONTRACT,
            Icon: AssignmentIndOutlinedIcon,
            title: 'My Contract',
            blurb: 'Your agreement, escrow balance, and strike history.',
            cta: 'View Contract',
        },
        {
            to: ROUTES.BRAND_MANAGE_CALENDAR,
            Icon: CalendarMonthOutlinedIcon,
            title: 'Content Calendar',
            blurb: 'Propose and publish content for this brand.',
            badge: stats ? `${stats.scheduled_post_count} Queued` : undefined,
            badgeColor: '#818cf8',
            meta: nextPost ? `Next: ${nextPost}` : undefined,
            cta: 'Open Timeline',
        },
        {
            to: ROUTES.BRAND_MANAGE_INBOX,
            Icon: InboxOutlinedIcon,
            title: 'Inbox',
            blurb: 'IG automation activity for brand posts.',
            cta: 'Open Inbox',
        },
        {
            to: ROUTES.BRAND_MANAGE_UGC,
            Icon: PhotoLibraryOutlinedIcon,
            title: 'Content Library',
            blurb: 'Browse approved UGC from your campaigns.',
            cta: 'Browse Vault',
        },
    ];

    const modules = isAdmin ? adminModules : managerModules;

    return (
        <BrandManageGate>
            <Box sx={MANAGE_PAGE_SX}>
                <BrandManagePromoBanner />

                {/* ── Header ── */}
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: 2,
                        mb: 4,
                    }}>
                    <Box>
                        {brand?.name && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Box
                                    sx={{
                                        width: 7,
                                        height: 7,
                                        borderRadius: '50%',
                                        bgcolor: '#34d399',
                                        boxShadow: '0 0 6px #34d399',
                                    }}
                                />
                                <Typography
                                    sx={{
                                        color: C_MUTED,
                                        fontSize: 11,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.1em',
                                    }}>
                                    Active Workspace
                                </Typography>
                                <Typography sx={{ color: 'rgba(255,255,255,0.2)', fontSize: 11 }}>/</Typography>
                                <Typography
                                    sx={{
                                        color: 'rgba(255,255,255,0.5)',
                                        fontSize: 11,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    }}>
                                    {brand.name}
                                </Typography>
                            </Box>
                        )}
                        <Typography
                            sx={{
                                color: '#fff',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: { xs: 34, md: 44 },
                                lineHeight: 1.1,
                                letterSpacing: '-1px',
                                mb: 1,
                            }}>
                            Social Manager
                        </Typography>
                        <Typography
                            sx={{
                                color: 'rgba(255,255,255,0.4)',
                                fontSize: 14,
                                lineHeight: 1.6,
                                maxWidth: 500,
                                fontFamily: FONT_ENUM.MAIN_REGULAR,
                            }}>
                            Content ops workspace — team, contracts, calendar, and social intelligence engineered for
                            creators and brand teams.
                        </Typography>
                    </Box>

                    {isAdmin && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1.25,
                                alignItems: 'center',
                                pt: { xs: 0, md: 0.5 },
                            }}>
                            <QuickBtn
                                to={ROUTES.BRAND_MANAGE_TEAM}
                                icon={<PersonAddOutlinedIcon sx={{ fontSize: 16 }} />}
                                label="Invite Manager"
                            />
                            <QuickBtn
                                to={ROUTES.BRAND_MANAGE_CALENDAR}
                                icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 16 }} />}
                                label="Schedule Post"
                            />
                            <QuickBtn
                                to={ROUTES.BRAND_MANAGE_CONTRACTS}
                                icon={<AddIcon sx={{ fontSize: 16 }} />}
                                label="New Contract"
                                primary
                            />
                        </Box>
                    )}
                </Box>

                {/* ── Stats bar — admin only ── */}
                {isAdmin && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
                        <StatTile
                            icon={<PeopleAltOutlinedIcon sx={{ fontSize: 18 }} />}
                            label="Active Managers"
                            value={stats ? String(stats.active_manager_count) : '—'}
                            sub="100% SLA"
                            loading={loadingStats && !stats}
                        />
                        <StatTile
                            icon={<MonetizationOnOutlinedIcon sx={{ fontSize: 18 }} />}
                            label="Live Contracts"
                            value={stats ? escrowDisplay : '—'}
                            sub={stats ? '/mo escrow' : undefined}
                            loading={loadingStats && !stats}
                        />
                        <StatTile
                            icon={<EventNoteOutlinedIcon sx={{ fontSize: 18 }} />}
                            label="Scheduled Posts"
                            value={stats ? String(stats.scheduled_post_count) : '—'}
                            sub="Omnichannel"
                            loading={loadingStats && !stats}
                        />
                        <StatTile
                            icon={<SmartToyOutlinedIcon sx={{ fontSize: 18 }} />}
                            label="AI Auto-Replies"
                            value={stats ? String(stats.auto_reply_count) : '—'}
                            sub="Last 30 days"
                            loading={loadingStats && !stats}
                        />
                    </Box>
                )}

                {/* ── Module grid ── */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            sm: '1fr 1fr',
                            lg: isAdmin ? '1fr 1fr 1fr' : '1fr 1fr',
                        },
                        gap: 2,
                    }}>
                    {modules.map(mod => (
                        <ModuleCard key={mod.to} {...mod} />
                    ))}
                </Box>
            </Box>
        </BrandManageGate>
    );
}
